import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Mail, Loader2, Check, User, Building2, MapPin, X } from 'lucide-react';

export const EmailDocketModal = ({ open, onClose, docketId, api }) => {
  const [recipients, setRecipients] = useState([]);
  const [docketNumber, setDocketNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [manualEmail, setManualEmail] = useState('');

  useEffect(() => {
    if (open && docketId) {
      fetchRecipients();
    }
    return () => {
      setRecipients([]);
      setSelectedEmail('');
      setManualEmail('');
      setLoading(true);
    };
  }, [open, docketId]);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/dockets/${docketId}/email-recipients`);
      setRecipients(res.data.recipients || []);
      setDocketNumber(res.data.docket_number || '');
      if (res.data.recipients?.length === 1) {
        setSelectedEmail(res.data.recipients[0].email);
      }
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const resolvedEmail = selectedEmail || manualEmail.trim();
  const isValid = emailRegex.test(resolvedEmail);

  const handleSend = async () => {
    if (!isValid || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/api/dockets/${docketId}/email`, { to_email: resolvedEmail });
      toast.success(res.data.message || `Docket queued for delivery to ${resolvedEmail}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send docket email');
    } finally {
      setSending(false);
    }
  };

  const sourceIcon = (source) => {
    if (source === 'customer') return <Building2 className="h-4 w-4 text-[#ffd108]" />;
    if (source === 'contact') return <User className="h-4 w-4 text-blue-400" />;
    return <MapPin className="h-4 w-4 text-emerald-400" />;
  };

  const sourceLabel = (source) => {
    if (source === 'customer') return 'Customer';
    if (source === 'contact') return 'Job Contact';
    return 'Site Contact';
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[1001]" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center p-4 z-[1002]">
        <div className="bg-[var(--m-bg)] border border-[var(--m-border)] rounded-2xl z-[1002] animate-in zoom-in-95 duration-200 max-h-[80vh] w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#ffd108]" />
            <h3 className="text-lg font-semibold text-[var(--m-text)]">
              Email Docket{docketNumber ? ` #${docketNumber}` : ''}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--m-bg-elevated)]" data-testid="email-docket-close">
            <X className="h-5 w-5 text-[var(--m-text-secondary)]" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-4 pb-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--m-text-secondary)]" />
            </div>
          ) : (
            <>
              {/* Suggested recipients */}
              {recipients.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-xs text-[var(--m-text-secondary)] uppercase tracking-wider">Select recipient</p>
                  {recipients.map((r) => (
                    <button
                      key={r.email}
                      data-testid={`email-recipient-${r.email}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        selectedEmail === r.email
                          ? 'border-[#ffd108] bg-[#ffd108]/10'
                          : 'border-[var(--m-border)] bg-[var(--m-bg-card)] hover:border-[var(--m-border-light)]'
                      }`}
                      onClick={() => { setSelectedEmail(r.email); setManualEmail(''); }}
                    >
                      <div className="shrink-0">
                        {selectedEmail === r.email
                          ? <Check className="h-5 w-5 text-[#ffd108]" />
                          : sourceIcon(r.source)
                        }
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[var(--m-text)] truncate">{r.name}</div>
                        <div className="text-xs text-[var(--m-text-secondary)] truncate">{r.email}</div>
                      </div>
                      <span className="text-[10px] text-[var(--m-text-muted)] uppercase shrink-0">{sourceLabel(r.source)}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Manual entry */}
              <div className="space-y-2">
                {recipients.length > 0 && (
                  <p className="text-xs text-[var(--m-text-secondary)] uppercase tracking-wider">Or enter email manually</p>
                )}
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={manualEmail}
                  onChange={(e) => { setManualEmail(e.target.value); setSelectedEmail(''); }}
                  className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] placeholder:text-[var(--m-text-muted)]"
                  data-testid="email-docket-manual-input"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 pt-2 border-t border-[var(--m-border)] shrink-0">
          <Button
            className="w-full btn-volt"
            disabled={!isValid || sending || loading}
            onClick={handleSend}
            data-testid="email-docket-send-btn"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send to {isValid ? resolvedEmail : '...'}
              </>
            )}
          </Button>
        </div>
        </div>
      </div>
    </>
  );
};
